/**
 * @module Services
 */

import EmberObject from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { isPresent, isEmpty } from '@ember/utils';
import { preciseRound } from '@latermedia/ember-later-analytics/utils';

import { filterTimeSeries } from 'later/utils/array-filters';

import type HelpersAnalyticsService from '../../helpers-analytics';
import type TableService from '../table';
import type { Moment } from 'moment';
import type { AnalyticsError } from 'shared/types/analytics-data';
import type { DefaultPost, ProcessedPost, RawPost } from 'shared/types/analytics-data/table-data';

/**
 * @class FormattersTablePostsService
 * @extends Service
 */
export default class PostsService extends Service {
  @service('analytics/helpers-analytics') declare helpersAnalytics: HelpersAnalyticsService;
  @service('analytics/formatters/table') declare tableFormatters: TableService;

  /**
   * Key names to be used to form table columns
   *
   * @property names
   * @type {Array}
   */
  names = ['likeCount', 'commentCount', 'reach', 'views', 'savedCount', 'shareCount', 'reposts', 'engagements'];

  /**
   * @property defaultRow
   * @type {Object}
   * @default
   */
  get defaultRow(): DefaultPost {
    return {
      id: undefined,
      url: undefined,
      instagramLink: undefined,
      type: undefined,
      isVideo: false,
      isCarousel: false,
      isDeleted: false,
      createdAt: '',
      createdAtTime: '',
      timestampLongFormat: '',
      createdAtUnix: undefined,
      caption: '-',
      followersCount: '-',
      engagements: this.tableFormatters.buildDefaultTableObject(),
      savedCount: this.tableFormatters.buildDefaultTableObject(),
      likeCount: this.tableFormatters.buildDefaultTableObject(),
      shareCount: this.tableFormatters.buildDefaultTableObject(),
      reposts: this.tableFormatters.buildDefaultTableObject(),
      commentCount: this.tableFormatters.buildDefaultTableObject(),
      scheduledPost: undefined
    };
  }

  /**
   * Takes an array of posts, formats each post for the
   * posts table according to the given post key names
   *
   * @method processPosts
   * @param {Array} [posts=[]] Array of unformatted posts
   * @param {Moment} startDate
   * @param {Moment} endDate
   * @param {Boolean} analyticsStandard
   *
   * @returns {Array} Array of posts formatted for the posts table
   */
  processPosts(
    posts: RawPost[] | AnalyticsError = [],
    startDate: Moment,
    endDate: Moment,
    analyticsStandard: boolean
  ): ProcessedPost[] | RawPost[] | AnalyticsError {
    if (isEmpty(posts) || !Array.isArray(posts)) {
      return posts as AnalyticsError;
    }

    const filteredPosts = analyticsStandard
      ? filterTimeSeries(posts, startDate.unix(), endDate.unix(), 'createdTime')
      : posts;

    return filteredPosts
      .map((post) => {
        const isCarousel = post.type === 'carousel';
        const values = this.tableFormatters.getValues(this.names, post, filteredPosts);
        const tableRow = this.tableFormatters.buildTableRow(values);

        const validLibClicks = this.tableFormatters.getValidValues(filteredPosts, 'linkinbioClicks');
        const scaledLibClicks = this.tableFormatters.scaleValue(
          validLibClicks,
          post.linkinbioClicks === 'N/A' ? 0 : Number(post.linkinbioClicks)
        );

        const videoViewsSanitized = post.type === 'video' ? post.videoViews : 'N/A';

        const engagementPercent = post.engagement * 100;

        const processedPost = Object.assign(
          {},
          {
            id: post.id ?? post.igId,
            url: post.thumbUrl,
            instagramLink: post.instagramLink,
            createdAt: this.helpersAnalytics.createMomentInTz(Number(post.createdTime)).format('MMM D, YYYY') || '-',
            createdAtTime: this.helpersAnalytics.createMomentInTz(Number(post.createdTime)).format('LT') || '-',
            timestampLongFormat:
              this.helpersAnalytics.createMomentInTz(Number(post.createdTime)).format('MMM D, YYYY hh:mm:ss A') || null,
            createdAtUnix: post.createdTime,
            caption: post.caption || null,
            type: post.type,
            isVideo: post.type === 'video',
            isCarousel,
            isDeleted: post.isDeleted,
            followersCount: post.followersOnPost,
            videoViews: {
              value: isPresent(videoViewsSanitized) ? videoViewsSanitized : '-',
              CSVValue: this.tableFormatters.isValid(videoViewsSanitized) ? post.videoViews : null,
              raw: this.tableFormatters.isValid(videoViewsSanitized) ? post.videoViews : null,
              scaled: this.tableFormatters.scaleValue(
                this.tableFormatters.getValidValues(posts, 'videoViews'),
                Number(post.videoViews)
              )
            },
            engagement: {
              value: this.tableFormatters.isValid(post.engagement) ? `${engagementPercent.toFixed(2)}%` : '-',
              CSVValue: this.tableFormatters.isValid(post.engagement) ? `${preciseRound(engagementPercent, 2)}%` : null,
              raw: this.tableFormatters.isValid(post.engagement) ? Number(post.engagement) : null,
              scaled: this.tableFormatters.createElementWidthAttr(post.engagement)
            },
            linkinbioClicks: { value: post.linkinbioClicks, raw: post.linkinbioClicks, scaled: scaledLibClicks },
            scheduledPost: post.scheduledPost
          },
          ...tableRow
        );
        return EmberObject.create(processedPost);
      })
      .reverse();
  }
}

declare module '@ember/service' {
  interface Registry {
    'analytics/formatters/table/posts': PostsService;
  }
}
